<script setup>
  import { computed } from "vue";
  import { Check, X, AlertTriangle, Info, Loader2 } from "lucide-vue-next";

  const props = defineProps({
    status: {
      type: String,
      required: true,
      validator: (v) => ["success", "error", "warning", "info", "loading"].includes(v),
    },
    title: { type: String, default: "" },
    message: { type: String, default: "" },
    progress: { type: Number, default: null },
    duration: { type: Number, default: 0 },
    actions: { type: Array, default: null },
    cancelLabel: { type: String, default: "Cancel" },
    closeLabel: { type: String, default: "Close" },
    onClose: { type: Function, default: null },
    onAction: { type: Function, default: null },
  });

  const accent = computed(() => {
    switch (props.status) {
      case "success": return "var(--color-status-success)";
      case "error": return "var(--color-status-error)";
      case "warning": return "var(--color-status-warning)";
      case "info": return "var(--color-status-info)";
      case "loading": return "var(--color-episto-navy)";
      default: return "var(--color-status-info)";
    }
  });

  const iconBg = computed(() => `color-mix(in oklab, ${accent.value} 14%, white)`);

  const showProgressBar = computed(() => typeof props.progress === "number");

  const showAutoDismissBar = computed(
    () => props.duration > 0 && props.status !== "error" && props.status !== "loading"
  );

  const closeAriaLabel = computed(() =>
    props.status === "loading" ? props.cancelLabel : props.closeLabel
  );

  const ariaRole = computed(() => (props.status === "error" ? "alert" : "status"));
  const ariaLive = computed(() => (props.status === "error" ? "assertive" : "polite"));
</script>

<template>
  <div
    class="toast group/toast relative overflow-hidden grid items-start w-[400px] max-w-[calc(100vw-40px)] py-3.5 pr-3.5 pl-4 bg-white border border-border rounded-lg shadow-lg font-sans transition-[border-left-color] duration-300 grid-cols-[22px_1fr_28px] gap-3"
    :style="{
      'border-left': `3px solid ${accent}`,
      '--accent': accent,
      '--toast-duration': `${duration}ms`,
    }"
    :role="ariaRole"
    :aria-live="ariaLive">
    <div
      class="w-[22px] h-[22px] mt-px rounded-full flex items-center justify-center shrink-0 transition-[background,color] duration-300"
      :style="{ background: iconBg, color: accent }">
      <Loader2
        v-if="status === 'loading'"
        :size="13"
        :stroke-width="2.6"
        class="animate-spin" />
      <Check
        v-else-if="status === 'success'"
        :size="13"
        :stroke-width="3" />
      <X
        v-else-if="status === 'error'"
        :size="14"
        :stroke-width="2.4" />
      <AlertTriangle
        v-else-if="status === 'warning'"
        :size="13"
        :stroke-width="2.5" />
      <Info
        v-else
        :size="13"
        :stroke-width="2.4" />
    </div>

    <div class="min-w-0">
      <p
        v-if="title"
        class="m-0 font-semibold text-13 leading-[1.2] tracking-[-0.005em] text-episto-navy break-words [overflow-wrap:anywhere]">
        {{ title }}
      </p>
      <p
        v-if="message"
        class="mt-[3px] mb-0 text-13 leading-[1.5] text-foreground-secondary break-words whitespace-pre-line [overflow-wrap:anywhere]"
        :class="{ 'tabular-nums': showProgressBar }">
        {{ message }}
      </p>
      <div
        v-if="showProgressBar"
        class="mt-2 flex items-center gap-2">
        <div class="flex-1 h-1 bg-muted rounded-full overflow-hidden">
          <div
            class="h-full rounded-full transition-[width,background] duration-300"
            :style="{
              width: `${Math.max(0, Math.min(100, progress))}%`,
              background: `color-mix(in oklab, ${accent} 40%, transparent)`,
            }" />
        </div>
        <span class="text-2xs font-semibold tabular-nums text-muted-foreground shrink-0">
          {{ Math.max(0, Math.min(100, Math.round(progress))) }}%
        </span>
      </div>
      <div
        v-if="actions && actions.length"
        class="flex gap-2 mt-2">
        <button
          v-for="action in actions"
          :key="action.label"
          type="button"
          class="px-2.5 py-1 text-xs font-semibold rounded-md border border-border bg-white text-episto-navy cursor-pointer transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline focus-visible:outline-offset-2"
          :style="{ outlineColor: accent }"
          @click="onAction && onAction(action)">
          {{ action.label }}
        </button>
      </div>
    </div>

    <button
      type="button"
      class="w-[26px] h-[26px] -mt-0.5 -mr-0.5 p-0 bg-transparent border-0 rounded-md cursor-pointer text-foreground-subtle flex items-center justify-center transition-[background,color] duration-200 hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline focus-visible:outline-offset-2"
      :style="{ outlineColor: accent }"
      :aria-label="closeAriaLabel"
      @click="onClose && onClose()">
      <X
        :size="14"
        :stroke-width="2.4" />
    </button>

    <div
      v-if="showAutoDismissBar"
      class="absolute bottom-0 left-0 h-0.5 w-full opacity-40 rounded-bl-lg rounded-br-lg origin-left group-hover/toast:[animation-play-state:paused] group-focus-within/toast:[animation-play-state:paused]"
      :style="{
        background: accent,
        animation: `toast-shrink ${duration}ms linear forwards`,
      }" />
  </div>
</template>
